import { useEffect, useRef, useState, type FC } from 'react';
import {
  DownOutlined,
  RightOutlined,
  PushpinOutlined,
  PushpinFilled,
  InfoCircleOutlined,
  FilterFilled,
} from '@ant-design/icons';
import { Input, Select, Switch, Table, TablePaginationConfig, Tooltip } from 'antd';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { Link, Space, Text, Button } from '@g2u/ui-kit';
import { format, parseISO, isValid } from 'date-fns';
import { useNavigate } from 'react-router';
import cx from 'classnames';
import he from 'he';
import env from 'env';

import {
  type JsonData,
  type SystemUser,
  type TicketTypeResponse,
  type JsonDataItem,
  type WithOptionalDataTest,
  Customer,
  Technician,
} from 'types';
import { TaskDueDate, CustomerSearchModal, TechnicianSearchModal } from 'components';
import { encryptAesToken, Toast } from 'helpers';
import { useScrollToRef } from 'hooks/useScrollToRef';
import { useTableSearch } from '../hooks';
import { PutTaskPayload, PutTicketParams } from 'queries';
import { TicketDetails } from '.';
import styles from '../tickets.module.scss';

type Props = {
  tickets?: JsonDataItem;
  ticketTypes?: TicketTypeResponse[];
  ticketAssignees?: SystemUser[];
  isLoading?: boolean;
  expandedRowKeys: number[];
  onExpand: (id: number) => void;
  onReopen: (args: { taskId: number }) => void;
  onClose: (args: { taskId: number; noteTypeId: number; note: string }) => void;
  onLock: ({ taskId }: { taskId: number }) => void;
  onUnlock: ({ taskId }: { taskId: number }) => void;
  onUpdateTicket: (payload: PutTicketParams) => void;
  onUpdateTask: (payload: PutTaskPayload) => void;
  onUpdateDueDate: (payload: { taskId: number; date: string }) => void;
  onPin: ({ taskId }: { taskId: number }) => void;
  onCallToCustomer: (number: string) => void;
  pagination: TablePaginationConfig;
  onFilter: (action: TicketsTable.FilterAction) => void;
  onPaginationChange: (config: JsonDataItem) => void;
  hasLogs: boolean;
  assigneeFilterId: number | null;
} & WithOptionalDataTest;

export const TicketsTable: FC<Props> = (props) => {
  const {
    ticketTypes,
    ticketAssignees,
    onReopen,
    onClose,
    onLock,
    onUnlock,
    onPin,
    onFilter,
    onUpdateTask,
    onUpdateTicket,
    onUpdateDueDate,
    onCallToCustomer,
    tickets,
    isLoading,
    pagination,
    onPaginationChange,
    expandedRowKeys,
    onExpand,
    hasLogs,
    assigneeFilterId,
  } = props;
  const [showCustomerSearch, setShowCustomerSearch] = useState<boolean>(false);
  const [showTechnicianSearch, setShowTechnicianSearch] = useState<boolean>(false);

  const navigate = useNavigate();

  const {
    options: assigneeOptions,
    search: assigneeSearch,
    handleSearch: handleAssigneeSearch,
  } = useTableSearch(ticketAssignees as JsonData, 'name', false);
  const {
    options: typeOptions,
    search: typeSearch,
    handleSearch: handleTypeSearch,
  } = useTableSearch(ticketTypes as JsonData, 'desc', true);

  const handleCallToCustomer = (number?: string) => {
    number
      ? onCallToCustomer(number)
      : Toast.show({
          description: 'No phone number in file',
          type: Toast.Type.Error,
        });
  };

  // autoscroll
  const { ref: scrollRef, scrollTo } = useScrollToRef<HTMLDivElement>();
  const hasScrolled = useRef(false);
  useEffect(() => {
    if (!hasScrolled.current && expandedRowKeys.length > 0 && !isLoading) {
      scrollTo({ offset: 142 });
      hasScrolled.current = true;
    }
  }, [expandedRowKeys]);

  return (
    <>
      <Table
        loading={isLoading}
        locale={{
          emptyText: isLoading ? <div style={{ height: 100 }} /> : undefined,
        }}
        columns={[
          {
            title: 'Ticket ID',
            dataIndex: 'ID',
            key: 'ID',
            render: (_text, record) => (
              <>
                {record.locked ? (
                  <PushpinFilled
                    className={cx(styles.tickets__icon, styles.tickets__icon_primary)}
                    onClick={() => onPin({ taskId: record.ID })}
                  />
                ) : (
                  <PushpinOutlined className={styles.tickets__icon} onClick={() => onPin({ taskId: record.ID })} />
                )}{' '}
                {record.record_ID}
              </>
            ),
            width: 100,
            minWidth: 100,
          },
          {
            title: 'Created By & Date',
            key: 'creationDate',
            render: (_text, record) =>
              record.record.created_at && isValid(parseISO(record.record.created_at)) ? (
                <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
                  {record.record.created_by_user && <Text.$>{he.decode(record.record.created_by_user)}</Text.$>}
                  <Space.$ size={Space.Size.ExtraSmall}>
                    <Text.$>{format(parseISO(record.record.created_at), 'dd MMM yyyy HH:mm')}</Text.$>
                  </Space.$>
                </Space.$>
              ) : (
                <Text.$>{'N/A'}</Text.$>
              ),
            minWidth: 165,
          },

          {
            title: 'Due Date & Time',
            dataIndex: 'due_date',
            key: 'dueDate',
            render: (text, record) => {
              return (
                <TaskDueDate
                  text={text}
                  record={record}
                  onDateUpdate={(data) => onUpdateDueDate({ taskId: record.ID, date: data.date })}
                />
              );
            },
            minWidth: 165,
          },
          {
            title: 'Ticket Type',
            key: 'taskTypeId',
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
              <Space.$ direction={Space.Direction.Vertical} style={{ padding: 12, minWidth: 280, maxWidth: 480 }}>
                <Select
                  placeholder="Select task type"
                  value={selectedKeys[0]}
                  onChange={(value) => {
                    setSelectedKeys([value]);
                  }}
                  popupMatchSelectWidth={false}
                  placement="bottomRight"
                  style={{ width: '100%' }}
                  options={ticketTypes?.map(({ task_type_ID, desc }) => ({
                    value: task_type_ID,
                    label: desc,
                  }))}
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
                <Space.$>
                  <Button.$
                    type={Button.Type.Primary}
                    size={Button.Size.Small}
                    dataTestId="apply-filter-btn"
                    onClick={() => {
                      onFilter({ kind: 'ticketType', id: selectedKeys[0] as number });
                      confirm();
                    }}
                  >
                    Search
                  </Button.$>
                  <Button.$
                    size={Button.Size.Small}
                    dataTestId="reset-filter-btn"
                    onClick={() => {
                      clearFilters?.();
                      onFilter({ kind: 'ticketType', id: null });
                      confirm({ closeDropdown: true });
                    }}
                  >
                    Reset
                  </Button.$>
                </Space.$>
              </Space.$>
            ),
            render: (_, record) => {
              const isTicketClosed = !!record.archive_datetime;
              return (
                <Select
                  placeholder="Unknown"
                  defaultValue={ticketTypes?.find((type) => type.ID === record.record.type.ID)?.desc}
                  className={cx(styles.tickets__select, isTicketClosed && styles.tickets__select_disabled)}
                  popupMatchSelectWidth={false}
                  placement="bottomRight"
                  options={
                    typeOptions
                      ? [...typeOptions].sort((a, b) => String(a.label).localeCompare(String(b.label)))
                      : undefined
                  }
                  dropdownRender={(menu) => (
                    <>
                      <Input
                        placeholder="Type to search"
                        value={typeSearch}
                        onChange={(e) => handleTypeSearch(e.target.value)}
                        autoFocus
                      />
                      {menu}
                    </>
                  )}
                  onBlur={() => handleTypeSearch('')}
                  onSelect={(id) => onUpdateTicket({ ticketId: record.record.ID, typeId: Number(id) })}
                />
              );
            },
            minWidth: 115,
          },
          {
            title: 'Job',
            key: 'job',
            render: (_text, record) =>
              record.record.job_ID ? (
                <Link.$ onClick={() => navigate(`/job/edit/${record.record.job_ID}/summary`)}>
                  #{record.record.job_ID}
                </Link.$>
              ) : (
                'N/A'
              ),
            width: 120,
            minWidth: 80,
          },
          {
            title: 'Customer ',
            filterIcon: <FilterFilled onClick={() => setShowCustomerSearch(true)} />,
            filterDropdown: () => <></>,
            key: 'customer',
            render: (_text, record) =>
              record.record.customer_ID ? (
                <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
                  <Text.$>{he.decode(record.task_links.customer?.name?.full_name || '')}</Text.$>
                  <Space.$ size={Space.Size.ExtraSmall}>
                    <Link.$ onClick={() => navigate(`/customer/edit/${record.record.customer_ID}/details`)}>
                      #{record.record.customer_ID}
                    </Link.$>
                    {record.task_links.customer && (
                      <Tooltip
                        title={
                          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
                            {record.task_links.customer?.contact?.[0]}
                            {record.task_links.customer?.email}
                          </Space.$>
                        }
                      >
                        <InfoCircleOutlined className={styles.tickets__icon} />
                      </Tooltip>
                    )}
                  </Space.$>
                </Space.$>
              ) : (
                <Text.$>{'Not Linked'}</Text.$>
              ),
            width: 150,
            minWidth: 100,
          },
          {
            title: 'Technician',
            filterIcon: <FilterFilled onClick={() => setShowTechnicianSearch(true)} />,
            filterDropdown: () => <></>,
            key: 'technician',
            render: (_text, record) => {
              const techId = record.task_links.technician?.ID;
              return techId ? (
                <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
                  <Text.$>{he.decode(record.task_links.technician?.name || '')}</Text.$>
                  <Space.$ size={Space.Size.ExtraSmall}>
                    <Link.$
                      // onClick={() => navigate(`/technician/edit/${techId}/details`)} TODO: use it when the Tech page is ready
                      target="_blank"
                      href={`${env.LEGACY_BMS_URL}technicians/index/edit/id/${encryptAesToken(techId?.toString())}`}
                    >
                      #{techId}
                    </Link.$>
                    {record.task_links.technician && (
                      <Tooltip
                        title={
                          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
                            {record.task_links.technician?.mobile?.[0]}
                            {record.task_links.technician?.email}
                          </Space.$>
                        }
                      >
                        <InfoCircleOutlined className={styles.tickets__icon} />
                      </Tooltip>
                    )}
                  </Space.$>
                </Space.$>
              ) : (
                <Text.$>{'Not Linked'}</Text.$>
              );
            },
            width: 150,
            minWidth: 100,
          },

          {
            title: 'Tech Visible',
            key: 'isTechVisible',
            render: (record) =>
              record.record.technician_ID ? (
                <>
                  <Switch
                    disabled={!!record.archive_datetime}
                    size="small"
                    defaultValue={Boolean(record.record.tech_visible)}
                    onChange={(value) => onUpdateTicket({ ticketId: record.record_ID, isTechVisible: value })}
                  />
                  <br /> <Text.$>{record.record.tech_visible ? 'Yes' : 'No'}</Text.$>
                </>
              ) : (
                'No'
              ),
            width: 65,
          },
          {
            title: 'Assignee',
            key: 'assignee',
            filteredValue: assigneeFilterId != null ? [assigneeFilterId] : null,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
              <Space.$ direction={Space.Direction.Vertical} style={{ padding: 12, minWidth: 280, maxWidth: 480 }}>
                <Select
                  placeholder="Select assignee"
                  value={selectedKeys[0]}
                  onChange={(value) => {
                    setSelectedKeys([value]);
                  }}
                  popupMatchSelectWidth={false}
                  placement="bottomRight"
                  style={{ width: '100%' }}
                  options={ticketAssignees
                    ?.toSorted((a, b) => a.name.localeCompare(b.name))
                    .map(({ ID, name }) => ({ value: ID, label: he.decode(name) }))}
                  showSearch
                  filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                />
                <Space.$>
                  <Button.$
                    type={Button.Type.Primary}
                    size={Button.Size.Small}
                    dataTestId="apply-filter-btn"
                    onClick={() => {
                      onFilter({ kind: 'userId', id: selectedKeys[0] as number });
                      confirm();
                    }}
                  >
                    Search
                  </Button.$>
                  <Button.$
                    size={Button.Size.Small}
                    dataTestId="reset-filter-btn"
                    onClick={() => {
                      clearFilters?.();
                      onFilter({ kind: 'userId', id: null });
                      confirm({ closeDropdown: true });
                    }}
                  >
                    Reset
                  </Button.$>
                </Space.$>
              </Space.$>
            ),
            render: (_, record) => {
              const isTicketClosed = !!record.archive_datetime;
              return (
                <Select
                  placeholder="Unassigned"
                  defaultValue={he.decode(
                    ticketAssignees?.find((user) => user.ID === record.assigned_ID)?.name || 'Unassigned',
                  )}
                  className={cx(styles.tickets__select, isTicketClosed && styles.tickets__select_disabled)}
                  popupMatchSelectWidth={false}
                  placement="bottomRight"
                  options={assigneeOptions}
                  dropdownRender={(menu) => (
                    <>
                      <Input
                        placeholder="Type to search"
                        value={assigneeSearch}
                        onChange={(e) => handleAssigneeSearch(e.target.value)}
                        autoFocus
                      />
                      {assigneeSearch ? menu : null}
                    </>
                  )}
                  onBlur={() => handleAssigneeSearch('')}
                  onSelect={(id) => onUpdateTask({ taskId: record.ID, assignedToId: Number(id) })}
                />
              );
            },
            width: 120,
            minWidth: 85,
          },
        ]}
        dataSource={tickets?.data ? [...tickets.data].sort((a, b) => b.record_ID - a.record_ID) : undefined}
        pagination={pagination}
        onChange={(rawPagination) => {
          onPaginationChange({
            page: rawPagination.current,
            pageSize: rawPagination.pageSize,
          });
        }}
        expandable={{
          onExpand: (_, record) => onExpand(record.record_ID),
          expandedRowKeys: expandedRowKeys as any,
          expandedRowRender: (record) => {
            return expandedRowKeys?.includes(record.record_ID) ? (
              <div ref={scrollRef}>
                <TicketDetails
                  record={record}
                  onClose={onClose}
                  onReopen={onReopen}
                  onLock={onLock}
                  onCallToCustomer={() => handleCallToCustomer(record.task_links.customer?.contact[0])}
                  onUnlock={onUnlock}
                  hasLogs={hasLogs}
                  onUpdateTicket={onUpdateTicket}
                />
              </div>
            ) : (
              <></>
            );
          },
          expandIcon: ({ expanded, onExpand, record }) =>
            expanded ? (
              <DownOutlined onClick={(e) => onExpand(record, e)} />
            ) : (
              <RightOutlined onClick={(e) => onExpand(record, e)} />
            ),
        }}
        rowKey="record_ID"
        className={styles.tickets__table}
      />
      <CustomerSearchModal
        customer={null}
        isOpen={showCustomerSearch}
        modalTitle={'Search Customer Files'}
        actions={[
          {
            title: 'Select',
            action: (record: Customer) => {
              onFilter({ kind: 'customer_id', id: record.ID });
              setShowCustomerSearch(false);
            },
          },
        ]}
        onOk={() => setShowCustomerSearch(false)}
        onCancel={() => setShowCustomerSearch(false)}
        emptyContent={() => null}
      />
      <TechnicianSearchModal
        customer={null}
        isOpen={showTechnicianSearch}
        modalTitle={'Search Technician Files'}
        actions={[
          {
            title: 'Select',
            action: (record: Technician) => {
              onFilter({ kind: 'technician_id', id: record.ID });
              setShowTechnicianSearch(false);
            },
          },
        ]}
        onOk={() => setShowTechnicianSearch(false)}
        onCancel={() => setShowTechnicianSearch(false)}
        emptyContent={() => null}
      />
    </>
  );
};

export namespace TicketsTable {
  export type FilterAction =
    | { kind: 'ticketType'; id: number | null }
    | { kind: 'userId'; id: number | null }
    | { kind: 'customer_id'; id: number }
    | { kind: 'technician_id'; id: number };
}
